import {
  ApiError,
  getLocationCheckinConfig,
  updateLocationCheckinConfig,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { CheckinConfigResponse, CheckinFieldId } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, Lock, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Types ─────────────────────────────────────────────────────────────────────

type ToggleState = Partial<Record<CheckinFieldId, boolean>>;

// ── Component ─────────────────────────────────────────────────────────────────

export function CheckinConfigPage() {
  const { t } = useTranslation('location-checkin-config');
  const { locationId } = useParams({ strict: false });

  const [config, setConfig] = useState<CheckinConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggles, setToggles] = useState<ToggleState>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const response = await getLocationCheckinConfig(locationId);
      setConfig(response);
      // Initialize toggle state from loaded config (exclude plate — always locked)
      const initialToggles: ToggleState = {};
      for (const field of response.fields) {
        if (!field.locked) {
          initialToggles[field.id] = field.required;
        }
      }
      setToggles(initialToggles);
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setSubmitting(true);
    setErrorBanner(null);
    try {
      const saved = await updateLocationCheckinConfig(locationId, { fields: toggles });
      setConfig(saved);
      // Sync toggle state from saved response
      const newToggles: ToggleState = {};
      for (const field of saved.fields) {
        if (!field.locked) {
          newToggles[field.id] = field.required;
        }
      }
      setToggles(newToggles);
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleToggle(fieldId: CheckinFieldId, value: boolean) {
    setToggles((prev) => ({ ...prev, [fieldId]: value }));
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <fieldset className="space-y-4">
                <legend className="text-[13px] font-medium text-jq-graphite">
                  {t('form.requiredFieldsLegend')}
                </legend>

                {config?.fields.map((field) => {
                  // hotel-only fields hidden when location is not hotel
                  if (field.nicheOnly === 'hotel' && config.locationNiche !== 'hotel') {
                    return null;
                  }

                  const isLocked = field.locked;
                  const checked = isLocked ? true : (toggles[field.id] ?? field.required);

                  return (
                    <div key={field.id} className="flex items-center gap-3">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          id={`field-${field.id}`}
                          checked={checked}
                          disabled={isLocked}
                          onChange={(e) => {
                            if (!isLocked) {
                              handleToggle(field.id, e.target.checked);
                            }
                          }}
                          className="h-4 w-4 rounded accent-jq-primary disabled:cursor-not-allowed"
                        />
                      </div>
                      <label
                        htmlFor={`field-${field.id}`}
                        className="flex items-center gap-2 text-[13px] text-jq-graphite"
                      >
                        {t(`fields.${fieldKey(field.id)}`)}
                        {isLocked && (
                          <span className="flex items-center gap-1 text-[11px] text-jq-graphite/60">
                            <Lock className="h-3 w-3" strokeWidth={1.75} />
                            {t('form.alwaysRequired')}
                          </span>
                        )}
                        {field.nicheOnly === 'hotel' && (
                          <span className="text-[11px] text-jq-graphite/60">
                            {t('form.hotelOnly')}
                          </span>
                        )}
                      </label>
                    </div>
                  );
                })}
              </fieldset>

              <Button type="submit" disabled={submitting}>
                {submitting ? t('form.saving') : t('form.save')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Maps the snake_case API field id to the camelCase i18n key under `fields.*`. */
function fieldKey(id: CheckinFieldId): string {
  const keys: Record<CheckinFieldId, string> = {
    plate: 'plate',
    first_name: 'firstName',
    last_name: 'lastName',
    phone: 'phone',
    visitor_email: 'visitorEmail',
    room_number: 'roomNumber',
    visitor_type: 'visitorType',
    parking_zone: 'parkingZone',
    valet_notes: 'valetNotes',
  };
  return keys[id];
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-checkin-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
